if (!customElements.get('pd-purchase-assist')) {
  customElements.define('pd-purchase-assist', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.frame = 0;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.abort = new AbortController();
      this.variants = JSON.parse(this.querySelector('[data-pd-variants]').textContent).variants;
      this.bar = this.querySelector('.pd-mobile-purchase');
      this.region = this.form.closest('product-info') || this.form.parentElement;
      this.directPurchase = this.dataset.directPurchase === 'true' && this.region.hasAttribute('data-pd-mobile-funnel') && !this.closest('quick-add-modal');
      this.dataset.directPurchase = String(this.directPurchase);
      this.strings = JSON.parse(this.querySelector('[data-pd-purchase-strings]')?.textContent || '{}');
      this.stickyButton = this.querySelector('[data-pd-review-options]');
      this.status = this.querySelector('[data-pd-mobile-status]');
      this.button = this.form.querySelector('[name="add"]');
      const queue = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      this.region.addEventListener('change', queue, { signal: this.abort.signal });
      this.region.addEventListener('input', queue, { signal: this.abort.signal });
      this.observer = new MutationObserver(records => { if (records.some(r => !this.contains(r.target))) queue(); });
      this.observer.observe(this.region, { subtree: true, childList: true, attributes: true, attributeFilter: ['value','checked','disabled','aria-disabled','class','hidden'] });
      this.visibility = new IntersectionObserver(entries => { this.purchaseVisible = entries[0].isIntersecting; this.updateBar(); });
      if (this.button) this.visibility.observe(this.button);
      window.addEventListener('scroll', () => this.updateBar(), { passive: true, signal: this.abort.signal });
      this.stickyButton.addEventListener('click', () => this.directPurchase ? this.submitNativeForm() : this.reviewOptions(), { signal: this.abort.signal });
      if (this.directPurchase) this.connectDirectPurchase(queue);
      this.update();
    }
    setText(selector, value) { const el = this.querySelector(selector); if (el.textContent !== value) el.textContent = value; }
    setPlanTerms(plan) {
      const el = this.querySelector('[data-pd-purchase-terms]');
      if (!el) return;
      const description = typeof plan?.description === 'string' ? plan.description.trim() : '';
      const terms = description === 'Minimum 2 paid orders, charged separately. Renews until cancelled; cancel future orders after your second paid order.'
        ? 'Renews automatically until cancelled. You can cancel future orders after your second paid order.'
        : description;
      if (el.textContent !== terms) el.textContent = terms;
      el.hidden = !terms;
    }
    setPriceDetail(value, prices = [], selector = '[data-pd-purchase-detail]') {
      const el = this.querySelector(selector);
      const amounts = [...new Set(prices.filter(price => typeof price === 'string' && price))].sort((a, b) => b.length - a.length);
      const key = JSON.stringify(amounts);
      if (el.textContent === value && el.dataset.priceKey === key) return;
      const fragment = document.createDocumentFragment();
      let offset = 0;
      while (offset < value.length) {
        let next = -1;
        let amount = '';
        amounts.forEach(price => {
          const index = value.indexOf(price, offset);
          if (index >= 0 && (next < 0 || index < next)) { next = index; amount = price; }
        });
        if (next < 0) { fragment.append(document.createTextNode(value.slice(offset))); break; }
        fragment.append(document.createTextNode(value.slice(offset, next)));
        const price = document.createElement('span');
        price.className = 'pd-purchase-price';
        price.textContent = amount;
        fragment.append(price);
        offset = next + amount.length;
      }
      el.replaceChildren(fragment);
      el.dataset.priceKey = key;
    }
    deliveryLabel(name) {
      const label = String(name || '').trim().replace(/\s*[—–-]\s*\d+(?:\.\d+)?%\s+off\s+from\s+order\s+\d+\s*$/i, '').trim();
      return label.replace(/^Monthly subscription$/i, 'Monthly');
    }
    updateDeliveryLabels() {
      this.region.querySelectorAll('.appstle_subscription_wrapper .appstleRadioSellingPlanWrapper label .sellingplan').forEach(label => {
        const compact = this.deliveryLabel(label.textContent);
        if (compact && label.textContent !== compact) label.textContent = compact;
      });
    }
    setHeroPrice(value) {
      const prefix = 'product-form-';
      if (!this.dataset.formId?.startsWith(prefix)) return;
      const price = document.getElementById(`price-${this.dataset.formId.slice(prefix.length)}`);
      if (!price || !this.region.contains(price) || this.region.querySelector('[id^="Volume-Note-"]')) return;
      price.querySelectorAll('.price__regular > span.price-item--regular, .price__sale > span.price-item--sale').forEach(amount => {
        if (amount.textContent.trim() !== value) amount.textContent = value;
      });
    }
    describePlan(plan) {
      const stages = Array.isArray(plan.price_adjustments) ? plan.price_adjustments.filter(stage => typeof stage.price === 'string' && stage.price) : [];
      const terms = `${this.deliveryLabel(plan.name)}.`;
      if (stages.length < 2 || stages.every(stage => stage.price === stages[0].price)) {
        return { detail: `${plan.price} per item · ${terms}`, mobile: `${plan.price} · Subscription` };
      }
      let firstOrder = 1;
      const prices = [];
      for (let index = 0; index < stages.length; index++) {
        const stage = stages[index];
        const count = stage.order_count;
        const hasCount = Number.isInteger(count) && count > 0;
        const finalStage = index === stages.length - 1;
        if (!hasCount && !finalStage) {
          return {
            detail: `Initial price: ${stages[0].price} per item. Later pricing: ${stages.slice(1).map(item => item.price).join(', ')} per item. ${this.deliveryLabel(plan.name)}. Review when prices change above.`,
            mobile: `Initial ${stages[0].price} · Review renewal terms`
          };
        }
        if (index === 0) {
          prices.push(`${count === 1 ? 'First order' : `First ${count} orders`}: ${stage.price} per item.`);
        } else if (!hasCount) {
          prices.push(`Then ${stage.price} per item from order ${firstOrder}.`);
        } else {
          const lastOrder = firstOrder + count - 1;
          prices.push(`${count === 1 ? `Order ${firstOrder}` : `Orders ${firstOrder}–${lastOrder}`}: ${stage.price} per item.`);
        }
        if (hasCount) firstOrder += count;
      }
      return {
        detail: `${prices.join(' ')} ${terms}`,
        mobile: `${stages[0].price} ${stages[0].order_count === 1 ? 'first' : `first ${stages[0].order_count} orders`} · ${stages[1].price} ${stages.length === 2 && stages[1].order_count == null ? 'renewals' : 'next'}`
      };
    }
    update() {
      this.updateDeliveryLabels();
      const variantId = this.form.querySelector('[name="id"]')?.value;
      const variant = this.variants.find(v => String(v.id) === String(variantId));
      if (!variant) {
        this.variantAvailable = null;
        this.selectionReady = false;
        this.setPlanTerms(null);
        this.querySelector('.pd-purchase-summary').hidden = false;
        this.setText('[data-pd-purchase-title]', 'Choose an available option');
        this.setText('[data-pd-purchase-detail]', 'Review the product options above for the current price and availability.');
        this.setText('[data-pd-mobile-detail]', 'Review product options');
        this.updateBar();
        return;
      }
      const selected = this.region.querySelector('.appstle_subscription_wrapper input[name="selling_plan"]:checked');
      const frequency = this.region.querySelector('input[name="selling_plan_radio"]:checked');
      const native = this.form.querySelector('input[type="hidden"][name="selling_plan"],select[name="selling_plan"]');
      const appPlanId = selected ? (selected.value ? (frequency?.value || selected.value) : '') : null;
      const state = this.directPurchase ? this.readNativeSelection() : null;
      const planId = state ? state.planId : (appPlanId ?? (native?.value || ''));
      this.selectionReady = Boolean(variant.available && (!state || state.valid) && (appPlanId === null || String(appPlanId) === String(planId)));
      this.currentVariantId = variantId;
      this.variantAvailable = variant.available;
      this.quantityValid = state?.quantityValid !== false;
      const plan = variant.plans.find(p => String(p.id) === String(planId));
      const recurring = Boolean(planId);
      if (recurring && !plan) this.selectionReady = false;
      this.setPlanTerms(recurring ? plan : null);
      const currentPrice = recurring ? plan?.price : variant.price;
      this.setHeroPrice(typeof currentPrice === 'string' && currentPrice ? currentPrice : 'See purchase options');
      const title = recurring ? 'Subscription selected' : 'One-time purchase selected';
      const planCopy = plan ? this.describePlan(plan) : null;
      const detail = planCopy ? planCopy.detail : recurring ? 'Recurring order. Confirm the price and delivery frequency in the purchase options above.' : `${variant.price} per item. One order, no recurring deliveries.`;
      this.setText('[data-pd-purchase-title]', title);
      this.setPriceDetail(detail, plan ? [plan.price, ...(plan.price_adjustments || []).map(stage => stage.price)] : [variant.price]);
      this.querySelector('.pd-purchase-summary').hidden = !recurring && variant.available;
      const mobileDetail = variant.available ? (recurring ? (planCopy?.mobile || 'Review price · Subscription') : `${variant.price} · One-time`) : 'Currently unavailable';
      this.setPriceDetail(mobileDetail, variant.available ? (plan ? [plan.price, ...(plan.price_adjustments || []).map(stage => stage.price)] : [variant.price]) : [], '[data-pd-mobile-detail]');
      this.dataset.recurring = String(recurring);
      if (this.directPurchase) {
        const selection = this.querySelector('[data-pd-mobile-selection]');
        const price = typeof currentPrice === 'string' ? currentPrice : this.strings.review;
        this.setPriceDetail(`${price} ${this.strings.perItem}`, [price], '[data-pd-mobile-detail]');
        selection.textContent = `${this.strings.quantity} ${state.quantityValid ? state.quantity : '—'} · ${recurring ? `${this.strings.subscription} · ${this.deliveryLabel(plan?.name)}` : this.strings.oneTime}`;
        selection.hidden = false;
        this.syncSubmitState();
      }
      this.updateBar();
    }
    updateBar() {
      if (!this.bar || !this.form) return;
      if (this.directPurchase) { this.updateDirectBar(); return; }
      const bounds = this.form.getBoundingClientRect();
      const below = bounds.height > 0 && bounds.bottom < 100;
      const overlay = document.querySelector('dialog[open],details[open] .search-modal,quick-add-modal[open],cart-drawer.active,details.menu-opening,#shopify-pc__banner:not([hidden])');
      this.bar.hidden = Boolean(this.closest('quick-add-modal') || this.purchaseVisible || !below || overlay);
    }
    reviewOptions() {
      const error = this.nativeError();
      const invalidQuantity = this.directPurchase && !this.readNativeSelection().quantityValid ? this.form.elements?.namedItem('quantity') : null;
      const target = invalidQuantity || error || this.region.querySelector('.appstle_subscription_wrapper') || this.form;
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      const input = invalidQuantity || error?.querySelector('a') || target.querySelector('input[type="radio"]:checked') || this.button;
      input?.focus({ preventScroll: true });
    }
    readNativeSelection() {
      const fields = new FormData(this.form);
      const planIds = [...new Set(fields.getAll('selling_plan').map(String).filter(Boolean))];
      const rawQuantity = fields.get('quantity');
      const quantity = rawQuantity === null || rawQuantity === undefined ? 1 : Number(rawQuantity);
      const quantityValid = (rawQuantity == null || String(rawQuantity).trim() !== '') && Number.isInteger(quantity) && quantity > 0;
      return { planId: planIds[0] || '', quantity, quantityValid, valid: planIds.length <= 1 && quantityValid };
    }
    connectDirectPurchase(queue) {
      const options = { signal: this.abort.signal };
      customElements.whenDefined('product-form').then(() => { if (this.isConnected) queue(); });
      this.form.addEventListener('submit', () => { this.setStatus(''); queue(); }, options);
      this.form.addEventListener('invalid', () => this.reviewOptions(), { ...options, capture: true });
      this.region.addEventListener('change', () => this.setStatus(''), options);
      this.overlayObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.updateDirectBar();
      });
      this.overlayObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open','hidden','class','style','aria-hidden','aria-modal'] });
      ['resize','orientationchange','pageshow','focus'].forEach(name => window.addEventListener(name, queue, options));
      ['visibilitychange','focusin','focusout','toggle','close'].forEach(name => document.addEventListener(name, queue, { ...options, capture: true }));
      window.visualViewport?.addEventListener('resize', queue, options);
      window.visualViewport?.addEventListener('scroll', queue, options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeSuccess = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'product-form' || String(event.productVariantId) !== String(this.currentVariantId)) return;
          this.setStatus(this.strings.added);
          queue();
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, event => {
          if (event.source !== 'product-form' || String(event.productVariantId) !== String(this.currentVariantId)) return;
          this.setStatus(this.strings.error);
          queue();
        });
      }
    }
    nativeError() {
      const error = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      return error && !error.hidden && error.textContent.trim() ? error : null;
    }
    setStatus(message, kind = '') {
      if (!this.status) return;
      this.statusKind = kind;
      if (this.status.textContent !== message) this.status.textContent = message;
      this.status.hidden = !message;
    }
    syncSubmitState() {
      this.button = this.form.querySelector('[name="add"]');
      if (!this.button) { this.selectionReady = false; return; }
      const owner = this.form.closest('product-form');
      this.nativeReady = typeof owner?.onSubmitHandler === 'function' && owner.submitButton === this.button;
      const loading = this.button.classList.contains('loading');
      const unavailable = this.button.disabled || this.button.getAttribute('aria-disabled') === 'true';
      const error = this.nativeError();
      this.stickyButton.disabled = loading || this.variantAvailable === false;
      this.stickyButton.setAttribute('aria-busy', String(loading));
      this.stickyButton.textContent = loading ? this.strings.loading : this.variantAvailable === false ? this.strings.unavailable : error || !this.nativeReady || !this.selectionReady ? this.strings.review : unavailable ? this.strings.unavailable : this.strings.add;
      if (this.variantAvailable === false && !loading) this.setStatus('');
      else if (error && !loading) this.setStatus(this.strings.error);
      else if (!this.nativeReady) this.setStatus(this.strings.initializing, 'initializing');
      else if (!this.selectionReady) this.setStatus(this.quantityValid ? this.strings.checkOptions : this.strings.checkQuantity, 'selection');
      else if (['selection', 'initializing'].includes(this.statusKind)) this.setStatus('');
    }
    submitNativeForm() {
      this.update();
      if (this.submitting || this.stickyButton.disabled) return;
      if (!this.nativeReady || !this.selectionReady || this.nativeError()) { this.reviewOptions(); return; }
      if (!this.button || this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
      if (!this.form.reportValidity()) { this.reviewOptions(); return; }
      this.submitting = true;
      try {
        this.form.requestSubmit(this.button);
        if (this.button.classList.contains('loading')) this.form.closest('product-form')?.cart?.setActiveElement?.(this.button);
      }
      finally { this.submitting = false; this.syncSubmitState(); }
    }
    isVisible(element) {
      if (!element || element.hidden || element.closest('[hidden],[inert],[aria-hidden="true"]')) return false;
      for (let node = element; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      }
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    }
    hasBlockingOverlay() {
      const selectors = 'dialog[open],product-modal[open],quick-add-modal[open],cart-drawer.active,cart-notification.active,details.menu-opening,details[open] .search-modal,[role="dialog"][aria-modal="true"],#shopify-pc__banner:not([hidden]),#shopify-pc__prefs:not([hidden])';
      return [...document.querySelectorAll(selectors)].some(element => this.isVisible(element));
    }
    updateDirectBar() {
      if (!this.bar || !this.button) return;
      this.syncSubmitState();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const rect = this.button.getBoundingClientRect();
      const pastMain = rect.height > 0 && rect.bottom <= top;
      const keyboard = Boolean(document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]),textarea,select,[contenteditable="true"]')) && viewport && viewport.height < window.innerHeight * .8;
      this.bar.hidden = Boolean(this.closest('quick-add-modal') || !pastMain || keyboard || document.hidden || this.hasBlockingOverlay() || /\/(?:checkouts?|account)(?:\/|$)/.test(window.location.pathname));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.overlayObserver?.disconnect();
      this.visibility?.disconnect();
      this.unsubscribeSuccess?.();
      this.unsubscribeError?.();
      cancelAnimationFrame(this.frame);
      this.abort = null;
      this.frame = 0;
    }
  });
}

